import { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions, AppModule, PermissionAction } from "@/hooks/usePermissions";
import { Shield } from "lucide-react";

interface PermissionGateProps {
  children: ReactNode;
  module: AppModule;
  action?: PermissionAction;
  fallback?: ReactNode;
}

export function PermissionGate({
  children,
  module,
  action = "view",
  fallback,
}: PermissionGateProps) {
  const { canAccess, loading } = usePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!canAccess(module, action)) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-foreground">Acceso Denegado</h3>
          <p className="text-sm text-muted-foreground">
            No tienes permisos para esta acción
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

interface RequirePermissionProps {
  children: ReactNode;
  module: AppModule;
  action?: PermissionAction;
  redirectTo?: string;
}

export function RequirePermission({
  children,
  module,
  action = "view",
  redirectTo = "/",
}: RequirePermissionProps) {
  const { canAccess, loading } = usePermissions();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!canAccess(module, action)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
